import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ClipboardText, Copy, ArrowsClockwise, X, CheckCircle, Sparkle, Envelope } from "@phosphor-icons/react";
import { fmtDateIN, isoDateIST } from "../lib/utils";
import { DateInput } from "../components/ui/date-input";

const STATUS_TONE = {
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  submitted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  converted: "bg-violet-50 text-violet-700 border-violet-200",
  cancelled: "bg-zinc-50 text-zinc-500 border-zinc-200",
  expired: "bg-red-50 text-red-700 border-red-200",
};

export default function JoinerForms() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    candidate_email: "", candidate_name: "", candidate_phone: "",
    proposed_doj: "", proposed_designation: "", proposed_ctc_annual: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/joiner-invite");
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const sendInvite = async () => {
    if (!draft.candidate_email || !draft.candidate_name) {
      return toast.error("Email and name are required");
    }
    setBusy(true);
    try {
      const r = await api.post("/joiner-invite/send", {
        ...draft,
        proposed_ctc_annual: draft.proposed_ctc_annual ? Number(draft.proposed_ctc_annual) : null,
      });
      if (r.data.already_exists) {
        toast.success("Invite already exists — link copied");
      } else {
        toast.success(`Invite sent to ${draft.candidate_email}`);
      }
      await navigator.clipboard?.writeText(r.data.magic_link).catch(() => {});
      setOpen(false);
      setDraft({ candidate_email: "", candidate_name: "", candidate_phone: "", proposed_doj: "", proposed_designation: "", proposed_ctc_annual: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const resend = async (id) => {
    try {
      await api.post(`/joiner-invite/${id}/resend`);
      toast.success("Reminder email sent");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this invitation? The link will stop working.")) return;
    try {
      await api.post(`/joiner-invite/${id}/cancel`);
      toast.success("Cancelled");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const convert = async (id) => {
    try {
      const r = await api.post(`/joiner-invite/${id}/convert-to-employee`);
      if (r.data.already_converted) {
        toast.success(`Already converted — employee ${r.data.employee_id}`);
      } else {
        toast.success(`Employee created${r.data.onboarding_id ? " + onboarding auto-started" : ""}`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link).then(() => toast.success("Link copied"));
  };

  const counts = {
    sent: rows.filter((r) => r.status === "sent").length,
    in_progress: rows.filter((r) => r.status === "in_progress").length,
    submitted: rows.filter((r) => r.status === "submitted").length,
    converted: rows.filter((r) => r.status === "converted").length,
  };

  return (
    <AppShell title="Joiner onboarding forms">

      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <Badge variant="outline" className="text-sm">{counts.sent} sent</Badge>
          <Badge variant="outline" className="text-sm bg-amber-50 border-amber-200">{counts.in_progress} in progress</Badge>
          <Badge variant="outline" className="text-sm bg-emerald-50 border-emerald-200">{counts.submitted} submitted — ready to convert</Badge>
          <Badge variant="outline" className="text-sm bg-violet-50 border-violet-200">{counts.converted} converted</Badge>
        </div>
        <Button className="bg-zinc-950 hover:bg-zinc-800" onClick={() => setOpen(true)} data-testid="send-invite-btn">
          <Sparkle size={16} className="mr-1.5" /> Send onboarding invite
        </Button>
      </div>

      <SectionCard title={`${rows.length} invitations`} subtitle="Each candidate gets a magic-link to a 16-section onboarding form. Reminders fire on Day 1, Day 3, and Day 7 automatically.">
        {rows.length === 0 ? (
          <div className="py-12 text-center text-zinc-500">No invitations yet. Click "Send onboarding invite" to begin.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => {
              const link = `${window.location.origin}/welcome/${r.id}.${(r.candidate_email || '').substring(0, 1)}`; // visual; real comes from /invitation/:id
              return (
                <div key={r.id} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`invite-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{r.candidate_name}</span>
                        <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-full ${STATUS_TONE[r.status] || "bg-zinc-50 border-zinc-200 text-zinc-600"}`} data-testid={`status-${r.id}`}>{r.status.replace("_", " ")}</span>
                        {r.completion_pct > 0 && <span className="text-xs text-zinc-500">{r.completion_pct}% filled</span>}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {r.candidate_email} {r.candidate_phone ? `· ${r.candidate_phone}` : ""}
                        {r.proposed_designation && <> · {r.proposed_designation}</>}
                        {r.proposed_doj && <> · DOJ {r.proposed_doj}</>}
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-1">
                        Sent {fmtDateIN(isoDateIST(r.created_at))}
                        {r.last_reminded_at && <> · last reminded {fmtDateIN(isoDateIST(r.last_reminded_at))}</>}
                        {r.submitted_at && <> · submitted {fmtDateIN(isoDateIST(r.submitted_at))}</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <InviteActions invite={r} onResend={() => resend(r.id)} onCancel={() => cancel(r.id)} onConvert={() => convert(r.id)} onCopyLink={copyLink} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="send-invite-dialog">
          <DialogHeader><DialogTitle>Send onboarding invite</DialogTitle>
            <DialogDescription>Candidate gets a magic-link email to fill personal, education, employment, banking, statutory, and policy-acknowledgment details. ~20 mins.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2"><Label>Candidate name *</Label><Input className="mt-1" value={draft.candidate_name} onChange={(e) => setDraft({ ...draft, candidate_name: e.target.value })} data-testid="invite-name" /></div>
            <div><Label>Email *</Label><Input className="mt-1" type="email" value={draft.candidate_email} onChange={(e) => setDraft({ ...draft, candidate_email: e.target.value })} data-testid="invite-email" /></div>
            <div><Label>Phone</Label><Input className="mt-1" value={draft.candidate_phone} onChange={(e) => setDraft({ ...draft, candidate_phone: e.target.value })} /></div>
            <div><Label>Proposed DOJ</Label><DateInput className="mt-1" value={draft.proposed_doj} onChange={(e) => setDraft({ ...draft, proposed_doj: e.target.value })} /></div>
            <div><Label>Designation</Label><Input className="mt-1" value={draft.proposed_designation} onChange={(e) => setDraft({ ...draft, proposed_designation: e.target.value })} /></div>
            <div className="col-span-2"><Label>Proposed annual CTC (₹)</Label><Input className="mt-1" type="number" value={draft.proposed_ctc_annual} onChange={(e) => setDraft({ ...draft, proposed_ctc_annual: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy} className="bg-zinc-950" onClick={sendInvite} data-testid="invite-confirm">{busy ? "Sending…" : "Send invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function InviteActions({ invite, onResend, onCancel, onConvert, onCopyLink }) {
  const [link, setLink] = useState("");
  const fetchLink = async () => {
    try {
      const r = await api.get(`/joiner-invite/${invite.id}`);
      setLink(r.data.magic_link);
      onCopyLink(r.data.magic_link);
    } catch { toast.error("Could not fetch link"); }
  };
  return (
    <>
      {invite.status === "submitted" && (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onConvert} data-testid={`convert-${invite.id}`}>
          <CheckCircle size={13} className="mr-1" /> Convert to employee
        </Button>
      )}
      {invite.status === "converted" && (
        <Link to="/app/employees" className="text-xs font-medium text-violet-700 hover:underline">View employee →</Link>
      )}
      {invite.status === "expired" && (
        <Button size="sm" variant="outline" onClick={onResend} data-testid={`resend-${invite.id}`}>
          <Envelope size={13} className="mr-1" /> Resend fresh link
        </Button>
      )}
      {(invite.status === "sent" || invite.status === "in_progress") && (
        <>
          <Button size="sm" variant="outline" onClick={onResend} data-testid={`resend-${invite.id}`}>
            <Envelope size={13} className="mr-1" /> Resend
          </Button>
          <Button size="sm" variant="ghost" onClick={fetchLink} data-testid={`copy-link-${invite.id}`}>
            <Copy size={13} className="mr-1" /> Copy link
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X size={13} className="text-zinc-500" />
          </Button>
        </>
      )}
    </>
  );
}
